import { useState, useEffect } from "react";
import { View, Text, TouchableOpacity, SafeAreaView, ScrollView, Alert, Platform, Switch, TextInput, ActivityIndicator, Image, Linking } from "react-native";
import { useRouter } from "expo-router";
import { useAuth } from "../lib/auth";
import { getNotificationPreferences, updateNotificationPreferences, getProfile, updateProfile, getDogs, apiRequest, forgotPassword } from "../lib/api";
import { Ionicons } from "@expo/vector-icons";
import Constants from "expo-constants";

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD",
  "MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC",
  "SD","TN","TX","UT","VT","VA","WA","WV","WI","WY",
];

const SUPER_ADMIN_EMAILS = ["gymboslice@gmail.com", "australiancattledogmom@gmail.com"];

// Pulled live from app.json so it auto-syncs every build
const APP_VERSION = Constants.expoConfig?.version ?? "1.0.0";

export default function SettingsScreen() {
  const router = useRouter();
  const { user, signOut, refreshUser } = useAuth();
  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState("");
  const [nameSaving, setNameSaving] = useState(false);
  const [nameError, setNameError] = useState("");
  const isAdmin = user?.email && SUPER_ADMIN_EMAILS.includes(user.email);
  const [rankingNotifs, setRankingNotifs] = useState(true);
  const [eventNotifs, setEventNotifs] = useState(true);
  const [followNotifs, setFollowNotifs] = useState(true);
  const [dogPhoto, setDogPhoto] = useState<string | null>(null);
  const [userState, setUserState] = useState("");
  const [userZip, setUserZip] = useState("");
  const [showStateDropdown, setShowStateDropdown] = useState(false);
  const [stateSearch, setStateSearch] = useState("");
  const [locationSaved, setLocationSaved] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  // "Forgot current password" — email a reset link so a logged-in user who never
  // signed out can still reset without knowing their old password.
  const [resetSending, setResetSending] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [feedbackName, setFeedbackName] = useState("");
  const [feedbackEmail, setFeedbackEmail] = useState("");
  const [feedbackMessage, setFeedbackMessage] = useState("");
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [feedbackSent, setFeedbackSent] = useState(false);
  const [feedbackError, setFeedbackError] = useState("");

  useEffect(() => {
    getNotificationPreferences()
      .then((prefs: any) => { setRankingNotifs(prefs.rankingChanges); setEventNotifs(prefs.eventAlerts !== false); setFollowNotifs(prefs.followAlerts !== false); })
      .catch(() => {});
    getProfile()
      .then((p) => { setUserState(p.state || ""); setUserZip(p.zipCode || ""); })
      .catch(() => {});
    getDogs()
      .then((d) => { const photo = d.find((dog) => dog.photo)?.photo; if (photo) setDogPhoto(photo); })
      .catch(() => {});
  }, []);

  async function toggleRankingNotifs(value: boolean) {
    setRankingNotifs(value);
    try {
      await updateNotificationPreferences({ rankingChanges: value });
    } catch {
      setRankingNotifs(!value); // revert on failure
    }
  }

  function handleLogout() {
    if (Platform.OS === "web") {
      if (confirm("Are you sure you want to sign out?")) {
        signOut().catch(() => {});
      }
    } else {
      Alert.alert(
        "Sign Out",
        "Are you sure you want to sign out?",
        [
          { text: "Cancel", style: "cancel" },
          {
            text: "Sign Out",
            style: "destructive",
            onPress: async () => {
              try {
                await signOut();
              } catch {}
            },
          },
        ]
      );
    }
  }

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: "#F4F6F8" }}>
      <ScrollView contentContainerStyle={{ padding: 20, paddingBottom: 60 }}>
        {/* User profile section */}
        <View
          style={{
            backgroundColor: "#ffffff",
            borderRadius: 16,
            padding: 20,
            marginBottom: 20,
            flexDirection: "row",
            alignItems: "center",
            borderWidth: 1,
            borderColor: "#e5e7eb",
          }}
        >
          {dogPhoto ? (
            <Image
              source={{ uri: dogPhoto }}
              style={{
                width: 60,
                height: 60,
                borderRadius: 30,
                marginRight: 16,
                borderWidth: 2,
                borderColor: "#e5e7eb",
              }}
            />
          ) : (
            <View
              style={{
                width: 60,
                height: 60,
                borderRadius: 30,
                backgroundColor: "#F0F2F5",
                alignItems: "center",
                justifyContent: "center",
                marginRight: 16,
              }}
            >
              <Ionicons name="paw" size={28} color="#F5A623" />
            </View>
          )}
          <View style={{ flex: 1 }}>
            {editingName ? (
              <View>
                <TextInput
                  value={nameInput}
                  onChangeText={(t) => { setNameInput(t); setNameError(""); }}
                  placeholder="Your name"
                  placeholderTextColor="#9ca3af"
                  autoCapitalize="words"
                  autoFocus
                  maxLength={100}
                  style={{ borderWidth: 1, borderColor: nameError ? "#dc2626" : "#1B2A4A", borderRadius: 8, paddingVertical: 8, paddingHorizontal: 10, fontSize: 16, color: "#111827", backgroundColor: "#f9fafb" }}
                />
                {nameError ? (
                  <Text style={{ color: "#dc2626", fontSize: 12, marginTop: 4 }}>{nameError}</Text>
                ) : null}
                <View style={{ flexDirection: "row", gap: 8, marginTop: 8 }}>
                  <TouchableOpacity
                    onPress={async () => {
                      const trimmed = nameInput.trim();
                      if (!trimmed) { setNameError("Name cannot be empty"); return; }
                      setNameSaving(true);
                      setNameError("");
                      try {
                        await updateProfile({ name: trimmed });
                        await refreshUser();
                        setEditingName(false);
                      } catch (err: any) {
                        setNameError(err?.message || "Failed to save");
                      } finally {
                        setNameSaving(false);
                      }
                    }}
                    disabled={nameSaving}
                    style={{ backgroundColor: "#1B2A4A", paddingVertical: 6, paddingHorizontal: 14, borderRadius: 8, opacity: nameSaving ? 0.6 : 1 }}
                  >
                    {nameSaving ? (
                      <ActivityIndicator size="small" color="#fff" />
                    ) : (
                      <Text style={{ color: "#fff", fontWeight: "700", fontSize: 13 }}>Save</Text>
                    )}
                  </TouchableOpacity>
                  <TouchableOpacity
                    onPress={() => { setEditingName(false); setNameError(""); }}
                    disabled={nameSaving}
                    style={{ paddingVertical: 6, paddingHorizontal: 14, borderRadius: 8, borderWidth: 1, borderColor: "#d1d5db" }}
                  >
                    <Text style={{ color: "#6b7280", fontWeight: "600", fontSize: 13 }}>Cancel</Text>
                  </TouchableOpacity>
                </View>
              </View>
            ) : (
              <TouchableOpacity
                onPress={() => { setNameInput(user?.name ?? ""); setEditingName(true); }}
                activeOpacity={0.7}
                style={{ flexDirection: "row", alignItems: "center" }}
              >
                <Text style={{ fontSize: 18, fontWeight: "700", color: "#1B2A4A" }}>
                  {user?.name ?? "—"}
                </Text>
                <Ionicons name="pencil" size={14} color="#9ca3af" style={{ marginLeft: 8 }} />
              </TouchableOpacity>
            )}
            <Text style={{ fontSize: 14, color: "#6b7280", marginTop: 3 }}>
              {user?.email ?? "—"}
            </Text>
          </View>
        </View>

        {/* App info section */}
        <Text style={{ fontSize: 14, fontWeight: "700", color: "#9ca3af", letterSpacing: 1, marginBottom: 8, marginLeft: 4, textTransform: "uppercase" }}>
          App Info
        </Text>
        <View
          style={{
            backgroundColor: "#ffffff",
            borderRadius: 14,
            borderWidth: 1,
            borderColor: "#e5e7eb",
            marginBottom: 24,
            overflow: "hidden",
          }}
        >
          <View
            style={{
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              padding: 16,
              borderBottomWidth: 1,
              borderBottomColor: "#f3f4f6",
            }}
          >
            <View style={{ flexDirection: "row", alignItems: "center" }}>
              <Ionicons name="information-circle-outline" size={20} color="#6b7280" />
              <Text style={{ marginLeft: 12, fontSize: 15, color: "#374151" }}>Version</Text>
            </View>
            <Text style={{ fontSize: 15, color: "#6b7280" }}>{APP_VERSION}</Text>
          </View>
          <View
            style={{
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              padding: 16,
            }}
          >
            <View style={{ flexDirection: "row", alignItems: "center" }}>
              <Ionicons name="paw-outline" size={20} color="#6b7280" />
              <Text style={{ marginLeft: 12, fontSize: 15, color: "#374151" }}>Dash-Hound</Text>
            </View>
            <Text style={{ fontSize: 15, color: "#6b7280" }}>AKC Fast CAT Tracker</Text>
          </View>
        </View>

        {/* Location section */}
        <Text style={{ fontSize: 14, fontWeight: "700", color: "#9ca3af", letterSpacing: 1, marginBottom: 8, marginLeft: 4, textTransform: "uppercase" }}>
          Location
        </Text>
        <View
          style={{
            backgroundColor: "#ffffff",
            borderRadius: 14,
            borderWidth: 1,
            borderColor: "#e5e7eb",
            marginBottom: 24,
            overflow: "hidden",
            padding: 16,
          }}
        >
          <Text style={{ fontSize: 13, color: "#6b7280", marginBottom: 12 }}>
            Set your location to get notified about events in your area.
          </Text>
          {/* State picker */}
          <View style={{ marginBottom: 10, zIndex: 10 }}>
            <TouchableOpacity
              onPress={() => setShowStateDropdown(!showStateDropdown)}
              style={{
                borderWidth: 1,
                borderColor: showStateDropdown ? "#1B2A4A" : "#d1d5db",
                borderRadius: 10,
                paddingVertical: 12,
                paddingHorizontal: 14,
                backgroundColor: "#f9fafb",
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Text style={{ fontSize: 15, color: userState ? "#111827" : "#9ca3af" }}>
                {userState || "Select state..."}
              </Text>
              <Text style={{ color: "#6b7280" }}>{showStateDropdown ? "▲" : "▼"}</Text>
            </TouchableOpacity>
            {showStateDropdown && (
              <View style={{ borderWidth: 1, borderColor: "#d1d5db", borderRadius: 10, backgroundColor: "#fff", marginTop: 4, maxHeight: 250, overflow: "hidden" }}>
                <TextInput
                  value={stateSearch}
                  onChangeText={setStateSearch}
                  placeholder="Search states..."
                  autoFocus
                  placeholderTextColor="#9ca3af"
                  style={{ borderBottomWidth: 1, borderBottomColor: "#e5e7eb", paddingVertical: 10, paddingHorizontal: 14, fontSize: 15, color: "#111827" }}
                />
                <ScrollView nestedScrollEnabled style={{ maxHeight: 200 }}>
                  {US_STATES.filter((s) => !stateSearch || s.toLowerCase().includes(stateSearch.toLowerCase())).map((s) => (
                    <TouchableOpacity
                      key={s}
                      onPress={() => { setUserState(s); setShowStateDropdown(false); setStateSearch(""); setLocationSaved(false); }}
                      style={{
                        paddingVertical: 11,
                        paddingHorizontal: 14,
                        backgroundColor: s === userState ? "#F0F2F5" : "#fff",
                        borderBottomWidth: 1,
                        borderBottomColor: "#f3f4f6",
                      }}
                    >
                      <Text style={{ fontSize: 15, color: s === userState ? "#1B2A4A" : "#111827", fontWeight: s === userState ? "600" : "400" }}>{s}</Text>
                    </TouchableOpacity>
                  ))}
                </ScrollView>
              </View>
            )}
          </View>
          {/* Zip code */}
          <TextInput
            value={userZip}
            onChangeText={(t) => { setUserZip(t); setLocationSaved(false); }}
            placeholder="Zip code"
            placeholderTextColor="#9ca3af"
            keyboardType="number-pad"
            style={{ borderWidth: 1, borderColor: "#d1d5db", borderRadius: 10, padding: 12, fontSize: 15, color: "#111827", backgroundColor: "#f9fafb", marginBottom: 12 }}
          />
          {/* Save button */}
          <TouchableOpacity
            onPress={async () => {
              await updateProfile({ state: userState, zipCode: userZip });
              setLocationSaved(true);
            }}
            style={{
              backgroundColor: locationSaved ? "#16a34a" : "#1B2A4A",
              borderRadius: 10,
              padding: 12,
              alignItems: "center",
            }}
          >
            <Text style={{ color: "#fff", fontWeight: "700", fontSize: 15 }}>
              {locationSaved ? "Saved!" : "Save Location"}
            </Text>
          </TouchableOpacity>
        </View>

        {/* Notifications section */}
        <Text style={{ fontSize: 14, fontWeight: "700", color: "#9ca3af", letterSpacing: 1, marginBottom: 8, marginLeft: 4, textTransform: "uppercase" }}>
          Notifications
        </Text>
        <View
          style={{
            backgroundColor: "#ffffff",
            borderRadius: 14,
            borderWidth: 1,
            borderColor: "#e5e7eb",
            marginBottom: 24,
            overflow: "hidden",
          }}
        >
          <View
            style={{
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              padding: 16,
            }}
          >
            <View style={{ flexDirection: "row", alignItems: "center", flex: 1 }}>
              <Ionicons name="trophy-outline" size={20} color="#6b7280" />
              <View style={{ marginLeft: 12, flex: 1 }}>
                <Text style={{ fontSize: 15, color: "#374151" }}>Ranking Updates</Text>
                <Text style={{ fontSize: 12, color: "#9ca3af", marginTop: 2 }}>Get emailed when your breed's rankings change</Text>
              </View>
            </View>
            <Switch
              value={rankingNotifs}
              onValueChange={toggleRankingNotifs}
              trackColor={{ false: "#d1d5db", true: "#F5A623" }}
              thumbColor="#ffffff"
            />
          </View>
          <View
            style={{
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              padding: 16,
              borderTopWidth: 1,
              borderTopColor: "#f3f4f6",
            }}
          >
            <View style={{ flexDirection: "row", alignItems: "center", flex: 1 }}>
              <Ionicons name="calendar-outline" size={20} color="#6b7280" />
              <View style={{ marginLeft: 12, flex: 1 }}>
                <Text style={{ fontSize: 15, color: "#374151" }}>New Event Alerts</Text>
                <Text style={{ fontSize: 12, color: "#9ca3af", marginTop: 2 }}>Get emailed when new events are posted in your state</Text>
              </View>
            </View>
            <Switch
              value={eventNotifs}
              onValueChange={async (val) => {
                setEventNotifs(val);
                try { await updateNotificationPreferences({ eventAlerts: val } as any); }
                catch { setEventNotifs(!val); }
              }}
              trackColor={{ false: "#d1d5db", true: "#F5A623" }}
              thumbColor="#ffffff"
            />
          </View>

          {/* Follow alerts — push when a dog you follow logs a run. */}
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              paddingVertical: 14,
              paddingHorizontal: 16,
              borderTopWidth: 1,
              borderTopColor: "#f3f4f6",
            }}
          >
            <View style={{ flexDirection: "row", alignItems: "center", flex: 1 }}>
              <Ionicons name="paw-outline" size={20} color="#6b7280" />
              <View style={{ marginLeft: 12, flex: 1 }}>
                <Text style={{ fontSize: 15, color: "#374151" }}>Followed Dog Alerts</Text>
                <Text style={{ fontSize: 12, color: "#9ca3af", marginTop: 2 }}>Push notification when a dog you follow logs a run</Text>
              </View>
            </View>
            <Switch
              value={followNotifs}
              onValueChange={async (val) => {
                setFollowNotifs(val);
                try { await updateNotificationPreferences({ followAlerts: val } as any); }
                catch { setFollowNotifs(!val); }
              }}
              trackColor={{ false: "#d1d5db", true: "#F5A623" }}
              thumbColor="#ffffff"
            />
          </View>
        </View>

        {/* Contact & Feedback section */}
        <Text style={{ fontSize: 14, fontWeight: "700", color: "#9ca3af", letterSpacing: 1, marginBottom: 8, marginLeft: 4, textTransform: "uppercase" }}>
          Feedback
        </Text>
        <View
          style={{
            backgroundColor: "#ffffff",
            borderRadius: 14,
            borderWidth: 1,
            borderColor: "#e5e7eb",
            marginBottom: 24,
            overflow: "hidden",
          }}
        >
          <TouchableOpacity
            onPress={() => { setShowFeedback(!showFeedback); setFeedbackSent(false); setFeedbackError(""); }}
            style={{
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
              padding: 16,
            }}
          >
            <View style={{ flexDirection: "row", alignItems: "center" }}>
              <Ionicons name="chatbubble-ellipses-outline" size={20} color="#6b7280" />
              <Text style={{ marginLeft: 12, fontSize: 15, color: "#374151" }}>Contact & Suggestions</Text>
            </View>
            <Ionicons name={showFeedback ? "chevron-up" : "chevron-down"} size={18} color="#9ca3af" />
          </TouchableOpacity>

          {showFeedback && (
            <View style={{ padding: 16, borderTopWidth: 1, borderTopColor: "#f3f4f6" }}>
              {feedbackSent ? (
                <View style={{ alignItems: "center", paddingVertical: 12 }}>
                  <Ionicons name="checkmark-circle" size={32} color="#16a34a" />
                  <Text style={{ color: "#16a34a", fontWeight: "700", fontSize: 16, marginTop: 8 }}>Thanks for your feedback!</Text>
                  <Text style={{ color: "#6b7280", fontSize: 13, marginTop: 4 }}>We'll get back to you if needed.</Text>
                </View>
              ) : (
                <>
                  {feedbackError ? (
                    <View style={{ backgroundColor: "#fef2f2", padding: 10, borderRadius: 8, marginBottom: 12 }}>
                      <Text style={{ color: "#dc2626", textAlign: "center", fontSize: 13 }}>{feedbackError}</Text>
                    </View>
                  ) : null}
                  <TextInput
                    value={feedbackName}
                    onChangeText={setFeedbackName}
                    placeholder="Your name"
                    placeholderTextColor="#9ca3af"
                    style={{ borderWidth: 1, borderColor: "#d1d5db", borderRadius: 10, padding: 12, fontSize: 15, color: "#111827", marginBottom: 10, backgroundColor: "#f9fafb" }}
                  />
                  <TextInput
                    value={feedbackEmail}
                    onChangeText={setFeedbackEmail}
                    placeholder="Your email"
                    placeholderTextColor="#9ca3af"
                    keyboardType="email-address"
                    autoCapitalize="none"
                    style={{ borderWidth: 1, borderColor: "#d1d5db", borderRadius: 10, padding: 12, fontSize: 15, color: "#111827", marginBottom: 10, backgroundColor: "#f9fafb" }}
                  />
                  <TextInput
                    value={feedbackMessage}
                    onChangeText={setFeedbackMessage}
                    placeholder="Your suggestion or feedback..."
                    placeholderTextColor="#9ca3af"
                    multiline
                    numberOfLines={4}
                    style={{ borderWidth: 1, borderColor: "#d1d5db", borderRadius: 10, padding: 12, fontSize: 15, color: "#111827", marginBottom: 14, backgroundColor: "#f9fafb", minHeight: 100, textAlignVertical: "top" }}
                  />
                  <TouchableOpacity
                    onPress={async () => {
                      if (!feedbackMessage.trim()) { setFeedbackError("Please enter a message"); return; }
                      setFeedbackLoading(true);
                      setFeedbackError("");
                      try {
                        const API_URL = (process.env.EXPO_PUBLIC_API_URL || "https://dashhound.app") + "/api";
                        const token = await (await import("../lib/storage")).getItem("accessToken");
                        await fetch(`${API_URL}/feedback`, {
                          method: "POST",
                          headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
                          body: JSON.stringify({ name: feedbackName.trim() || user?.name || "Anonymous", email: feedbackEmail.trim() || user?.email || "", message: feedbackMessage.trim() }),
                        });
                        setFeedbackSent(true);
                        setFeedbackName(""); setFeedbackEmail(""); setFeedbackMessage("");
                      } catch { setFeedbackError("Failed to send. Please try again."); }
                      finally { setFeedbackLoading(false); }
                    }}
                    disabled={feedbackLoading}
                    style={{
                      backgroundColor: "#1B2A4A",
                      borderRadius: 10,
                      padding: 14,
                      alignItems: "center",
                      opacity: feedbackLoading ? 0.6 : 1,
                    }}
                  >
                    {feedbackLoading ? (
                      <ActivityIndicator color="#fff" />
                    ) : (
                      <Text style={{ color: "#fff", fontSize: 16, fontWeight: "700" }}>Send Feedback</Text>
                    )}
                  </TouchableOpacity>
                </>
              )}
            </View>
          )}
        </View>

        {/* Change Password section */}
        <Text style={{ fontSize: 14, fontWeight: "700", color: "#9ca3af", letterSpacing: 1, marginBottom: 8, marginLeft: 4, textTransform: "uppercase" }}>
          Password
        </Text>
        <View
          style={{
            backgroundColor: "#ffffff",
            borderRadius: 14,
            borderWidth: 1,
            borderColor: "#e5e7eb",
            marginBottom: 24,
            overflow: "hidden",
          }}
        >
          <TouchableOpacity
            onPress={() => { setShowChangePassword(!showChangePassword); setPasswordSuccess(false); setPasswordError(""); }}
            style={{
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
              padding: 16,
            }}
          >
            <View style={{ flexDirection: "row", alignItems: "center" }}>
              <Ionicons name="lock-closed-outline" size={20} color="#6b7280" />
              <Text style={{ marginLeft: 12, fontSize: 15, color: "#374151" }}>Change Password</Text>
            </View>
            <Ionicons name={showChangePassword ? "chevron-up" : "chevron-down"} size={18} color="#9ca3af" />
          </TouchableOpacity>

          {showChangePassword && (
            <View style={{ padding: 16, borderTopWidth: 1, borderTopColor: "#f3f4f6" }}>
              {passwordSuccess ? (
                <View style={{ alignItems: "center", paddingVertical: 12 }}>
                  <Ionicons name="checkmark-circle" size={32} color="#16a34a" />
                  <Text style={{ color: "#16a34a", fontWeight: "700", fontSize: 16, marginTop: 8 }}>Password changed!</Text>
                </View>
              ) : (
                <>
                  {passwordError ? (
                    <View style={{ backgroundColor: "#fef2f2", padding: 10, borderRadius: 8, marginBottom: 12 }}>
                      <Text style={{ color: "#dc2626", textAlign: "center", fontSize: 13 }}>{passwordError}</Text>
                    </View>
                  ) : null}
                  <TextInput
                    value={currentPassword}
                    onChangeText={setCurrentPassword}
                    placeholder="Current password"
                    placeholderTextColor="#9ca3af"
                    secureTextEntry
                    style={{ borderWidth: 1, borderColor: "#d1d5db", borderRadius: 10, padding: 12, fontSize: 15, color: "#111827", marginBottom: 10, backgroundColor: "#f9fafb" }}
                  />
                  <TextInput
                    value={newPassword}
                    onChangeText={setNewPassword}
                    placeholder="New password"
                    placeholderTextColor="#9ca3af"
                    secureTextEntry
                    style={{ borderWidth: 1, borderColor: "#d1d5db", borderRadius: 10, padding: 12, fontSize: 15, color: "#111827", marginBottom: 10, backgroundColor: "#f9fafb" }}
                  />
                  <TextInput
                    value={confirmPassword}
                    onChangeText={setConfirmPassword}
                    placeholder="Confirm new password"
                    placeholderTextColor="#9ca3af"
                    secureTextEntry
                    style={{ borderWidth: 1, borderColor: "#d1d5db", borderRadius: 10, padding: 12, fontSize: 15, color: "#111827", marginBottom: 14, backgroundColor: "#f9fafb" }}
                  />
                  <TouchableOpacity
                    onPress={async () => {
                      if (!currentPassword || !newPassword || !confirmPassword) { setPasswordError("Please fill in all fields"); return; }
                      if (newPassword.length < 10) { setPasswordError("New password must be at least 10 characters"); return; }
                      if (newPassword !== confirmPassword) { setPasswordError("Passwords don't match"); return; }
                      setPasswordLoading(true);
                      setPasswordError("");
                      try {
                        await apiRequest("/auth/change-password", { method: "PUT", body: JSON.stringify({ currentPassword, newPassword }) });
                        setPasswordSuccess(true);
                        setCurrentPassword(""); setNewPassword(""); setConfirmPassword("");
                      } catch (err: any) { setPasswordError(err.message || "Failed to change password"); }
                      finally { setPasswordLoading(false); }
                    }}
                    disabled={passwordLoading}
                    style={{
                      backgroundColor: "#1B2A4A",
                      borderRadius: 10,
                      padding: 14,
                      alignItems: "center",
                      opacity: passwordLoading ? 0.6 : 1,
                    }}
                  >
                    {passwordLoading ? (
                      <ActivityIndicator color="#fff" />
                    ) : (
                      <Text style={{ color: "#fff", fontSize: 16, fontWeight: "700" }}>Change Password</Text>
                    )}
                  </TouchableOpacity>

                  {/* Forgot the current password? Email a reset link (no old password needed). */}
                  {resetSent ? (
                    <Text style={{ color: "#16a34a", fontSize: 13, textAlign: "center", marginTop: 14 }}>
                      Reset link sent to {user?.email}. Check your email (and spam folder) — the link is good for 1 hour.
                    </Text>
                  ) : (
                    <TouchableOpacity
                      disabled={resetSending}
                      onPress={async () => {
                        if (!user?.email) { setPasswordError("No email on file for your account."); return; }
                        setResetSending(true);
                        setPasswordError("");
                        try {
                          await forgotPassword(user.email);
                          setResetSent(true);
                        } catch (err: any) {
                          setPasswordError(err.message || "Could not send reset link");
                        } finally { setResetSending(false); }
                      }}
                      style={{ marginTop: 14, alignItems: "center" }}
                    >
                      <Text style={{ color: "#1B2A4A", fontSize: 13, fontWeight: "600", textDecorationLine: "underline" }}>
                        {resetSending ? "Sending…" : "Forgot your current password? Email me a reset link"}
                      </Text>
                    </TouchableOpacity>
                  )}
                </>
              )}
            </View>
          )}
        </View>

        {/* Resources section — reachable while logged in (Blog/Partners are
            whitelisted in the route guard; Shop is the external store). */}
        <Text style={{ fontSize: 14, fontWeight: "700", color: "#9ca3af", letterSpacing: 1, marginBottom: 8, marginLeft: 4, textTransform: "uppercase" }}>
          Resources
        </Text>
        <View
          style={{
            backgroundColor: "#ffffff",
            borderRadius: 14,
            borderWidth: 1,
            borderColor: "#e5e7eb",
            overflow: "hidden",
            marginBottom: 24,
          }}
        >
          {[
            { icon: "newspaper-outline", label: "Blog", external: false, onPress: () => (Platform.OS === "web" ? router.push("/(auth)/blog" as any) : Linking.openURL("https://dashhound.app/blog")) },
            { icon: "people-outline", label: "Partners", external: false, onPress: () => router.push("/(auth)/partners" as any) },
            { icon: "bag-outline", label: "Shop", external: true, onPress: () => Linking.openURL("https://dashhound.app/shop") },
            { icon: "help-circle-outline", label: "Support & FAQ", external: false, onPress: () => router.push("/(auth)/support/" as any) },
          ].map((row, i, arr) => (
            <TouchableOpacity
              key={row.label}
              onPress={row.onPress}
              style={{
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                padding: 16,
                borderBottomWidth: i < arr.length - 1 ? 1 : 0,
                borderBottomColor: "#f3f4f6",
              }}
            >
              <View style={{ flexDirection: "row", alignItems: "center" }}>
                <Ionicons name={row.icon as any} size={20} color="#6b7280" />
                <Text style={{ marginLeft: 12, fontSize: 15, color: "#374151" }}>{row.label}</Text>
              </View>
              <Ionicons name={row.external ? "open-outline" : "chevron-forward"} size={16} color="#9ca3af" />
            </TouchableOpacity>
          ))}
        </View>

        {/* Account section */}
        <Text style={{ fontSize: 14, fontWeight: "700", color: "#9ca3af", letterSpacing: 1, marginBottom: 8, marginLeft: 4, textTransform: "uppercase" }}>
          Account
        </Text>
        <View
          style={{
            backgroundColor: "#ffffff",
            borderRadius: 14,
            borderWidth: 1,
            borderColor: "#e5e7eb",
            overflow: "hidden",
          }}
        >
          <TouchableOpacity
            onPress={handleLogout}
            style={{
              flexDirection: "row",
              alignItems: "center",
              padding: 16,
            }}
          >
            <Ionicons name="log-out-outline" size={20} color="#dc2626" />
            <Text style={{ marginLeft: 12, fontSize: 15, color: "#dc2626", fontWeight: "600" }}>
              Sign Out
            </Text>
          </TouchableOpacity>
        </View>

        {/* Admin link — only visible to super admins */}
        {isAdmin && (
          <>
            <View style={{ marginTop: 8 }} />
            <TouchableOpacity
              onPress={() => router.push("/dashadmin" as any)}
              style={{
                backgroundColor: "#1B2A4A",
                borderRadius: 14,
                padding: 16,
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
              }}
            >
              <Ionicons name="shield-checkmark" size={20} color="#F5A623" />
              <Text style={{ color: "#fff", fontSize: 15, fontWeight: "700" }}>Admin Dashboard</Text>
            </TouchableOpacity>
          </>
        )}
        {/* Delete Account */}
        <Text style={{ fontSize: 14, fontWeight: "700", color: "#dc2626", letterSpacing: 1, marginBottom: 8, marginLeft: 4, marginTop: 16, textTransform: "uppercase" }}>
          Danger Zone
        </Text>
        <View
          style={{
            backgroundColor: "#fef2f2",
            borderRadius: 14,
            borderWidth: 2,
            borderColor: "#dc2626",
            marginBottom: 40,
            overflow: "hidden",
            padding: 16,
          }}
        >
          <Text style={{ color: "#991b1b", fontSize: 13, lineHeight: 18, marginBottom: 12, textAlign: "center" }}>
            Permanently delete your account and all data including dogs, runs, and photos. This cannot be undone.
          </Text>
          <TouchableOpacity
            onPress={async () => {
              if (Platform.OS === "web") {
                if (!confirm("Are you sure you want to delete your account? This will permanently delete all your dogs, runs, and data. This cannot be undone.")) return;
              } else {
                const confirmed = await new Promise((resolve) => {
                  Alert.alert(
                    "Delete Account",
                    "Are you sure? This will permanently delete all your dogs, runs, and data. This cannot be undone.",
                    [
                      { text: "Cancel", style: "cancel", onPress: () => resolve(false) },
                      { text: "Delete", style: "destructive", onPress: () => resolve(true) },
                    ]
                  );
                });
                if (!confirmed) return;
              }
              try {
                await apiRequest("/auth/account", { method: "DELETE" });
                await signOut();
              } catch {}
            }}
            style={{
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "center",
              padding: 14,
              gap: 8,
              backgroundColor: "#dc2626",
              borderRadius: 10,
            }}
          >
            <Ionicons name="trash-outline" size={20} color="#ffffff" />
            <Text style={{ color: "#ffffff", fontSize: 15, fontWeight: "700" }}>Delete Account</Text>
          </TouchableOpacity>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}
